import React, { useState, useEffect } from 'react';
import {Box, Button, Checkbox, FormControlLabel, TextField, Typography, Paper, Avatar, Grid} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../../utils/apiClient';

const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const AddEditVehicleBrand = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const brandData = location.state;

  const [brandName, setBrandName] = useState('');
  const [brandLogo, setBrandLogo] = useState(null);
  const [existingLogo, setExistingLogo] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState('');
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (brandData) {
      setBrandName(brandData.brandName || '');
      setIsActive(brandData.isActive || false);
      setExistingLogo(brandData.brandLogo || null);
    }
  }, [brandData]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    const MAX_FILE_SIZE = 50 * 1024; 

    if (!allowedTypes.includes(file.type)) {
      return setError('Only JPG, PNG and WEBP files are allowed');
    } else if (file.size > MAX_FILE_SIZE) {
      return setError('File size must be less than 50KB');
    } else setError('');

    setBrandLogo(file);
  };

  const handleSubmit = async () => {
    if (!brandName || (!brandLogo && !existingLogo)) {
      return setError('Please fill all required fields.');
    }
    const formData = new FormData();
    formData.append('brandName', brandName);
    formData.append('isActive', isActive);

    if (brandLogo) {
      formData.append('image', brandLogo);
    }

    try {
      setDisabled(true);
      if (brandData) {
        const res = await apiClient.put(`/vehiclebrand/updatebrand/${brandData.id}`,
          formData, {headers: {'Content-Type': 'multipart/form-data'}});
        if (res.data.status === true) {
          toast.success(res.data.message);
          navigate('/display-vehicle-brands');
        }
      } else {
        const res = await apiClient.post('/vehiclebrand/create', formData, {
          headers: {'Content-Type': 'multipart/form-data'}});
        if (res.data.status === true) {
          toast.success(res.data.message);
          navigate('/display-vehicle-brands');
        }
      }
    } catch (err) {
      setDisabled(false);
      setError(err?.response?.data?.message || 'Error submitting brand. Please try again.');
    }
  };

  return (
    <Box sx={{ maxWidth: 500, mr: 'auto', mt: 1.5, p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant='h4' mb={3} align='center'>
          {brandData ? 'Edit Vehicle Brand' : 'Add New Vehicle Brand'}
        </Typography>
        <Grid container spacing={2}>
          <Grid item size={12}>
            <TextField fullWidth label='Brand Name*' value={brandName}
              onChange={(e) => { setError(''); setBrandName(e.target.value) }}
            />
          </Grid>
          <Grid item size={12}>
            <Button variant='contained' component='label' sx={{ width: '100%' }} >
              Upload Logo*
              <input type='file' hidden accept='image/*' onChange={handleImageChange} />
            </Button>
          </Grid>
          {(brandLogo || existingLogo) && (
            <Grid item size={12} sx={{ textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'> Image Preview: </Typography>
              <Box display='flex' justifyContent='center' mt={1}>
                <Avatar src={ brandLogo ? URL.createObjectURL(brandLogo) : existingLogo }
                  alt='Brand Logo' variant='square' sx={{ width: 80, height: 80 }} />
              </Box>
            </Grid>
          )}
          <Grid item size={12}>
            <FormControlLabel control={
                <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              } label='Is Active'
            />
          </Grid>
          {error && (
            <Grid item size={12}>
              <Typography color='error'>{error}</Typography>
            </Grid>
          )}
          <Grid item size={12} display='flex' gap={2} justifyContent='flex-end'>
            <Button disabled={disabled} variant='contained' onClick={handleSubmit}> Submit </Button>
            <Button disabled={disabled} variant='outlined' onClick={() => navigate('/display-vehicle-brands')}> Cancel </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default AddEditVehicleBrand;
